// A synthesized menu, recorded music loops, and synthesized sound effects.
// The recorded songs live in assets/music and are handed in as base64 before this bundle runs.

const NOTE = (n) => 440 * Math.pow(2, (n - 69) / 12);

class AudioEngine {
  constructor() {
    this.ctx = null;
    this.vol = { master: 0.8, music: 0.6, sfx: 0.8 };
    this.songName = null;
    this.song = null;
    this.step = 0;
    this.nextTime = 0;
    this.tempoMul = 1;
    this.songBuffers = {};
    this.songDecodes = {};
    this.musicSource = null;
    this.intensity = 0;
    this.loops = {};
    this.muted = false;
  }
  init() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = (this.ctx = new AC());
    this.master = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 4;
    this.master.connect(comp).connect(ctx.destination);
    this.musicBus = ctx.createGain();
    this.sfxBus = ctx.createGain();
    this.musicBus.connect(this.master);
    this.sfxBus.connect(this.master);
    // reverb
    this.reverb = ctx.createConvolver();
    this.reverb.buffer = this._impulse(3.2, 2.2);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 0.5;
    this.reverbSend.connect(this.reverb).connect(this.master);
    // delay
    this.delay = ctx.createDelay(2);
    this.delay.delayTime.value = 0.375;
    const fb = ctx.createGain();
    fb.gain.value = 0.38;
    const dl = ctx.createBiquadFilter();
    dl.type = 'lowpass';
    dl.frequency.value = 2600;
    this.delay.connect(dl).connect(fb).connect(this.delay);
    this.delaySend = ctx.createGain();
    this.delaySend.gain.value = 0.35;
    this.delaySend.connect(this.delay);
    dl.connect(this.musicBus);
    this.noiseBuf = this._noise(2);
    this.applyVolumes();
    this._timer = setInterval(() => this._schedule(), 25);
  }
  applyVolumes() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.vol.master, t, 0.05);
    this.musicBus.gain.setTargetAtTime(this.vol.music * 0.55, t, 0.05);
    this.sfxBus.gain.setTargetAtTime(this.vol.sfx, t, 0.05);
    this.reverbSend.gain.setTargetAtTime(0.5 * Math.max(this.vol.music, 0.2), t, 0.05);
  }
  _impulse(dur, decay) {
    const ctx = this.ctx, len = ctx.sampleRate * dur;
    const b = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = b.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return b;
  }
  _noise(dur) {
    const ctx = this.ctx, len = ctx.sampleRate * dur;
    const b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }

  // ------------------------------------------------------------ menu instruments
  _env(g, t, a, peak, dec, sus = 0, rel = 0.1, dur = 0) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + a);
    if (dur) {
      g.gain.setTargetAtTime(sus * peak, t + a, dec / 3);
      g.gain.setTargetAtTime(0.0001, t + dur, rel / 3);
    } else g.gain.exponentialRampToValueAtTime(0.0001, t + a + dec);
  }
  _osc(type, f, t, stop, dest, detune = 0) {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f, t);
    o.detune.value = detune;
    o.connect(dest);
    o.start(t);
    o.stop(stop);
    return o;
  }
  pad(notes, t, dur, vol = 0.05, bright = 900) {
    const ctx = this.ctx;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(bright * 0.6, t);
    f.frequency.linearRampToValueAtTime(bright, t + dur * 0.5);
    f.frequency.linearRampToValueAtTime(bright * 0.7, t + dur);
    f.Q.value = 0.7;
    const g = ctx.createGain();
    this._env(g, t, Math.min(1.5, dur * 0.3), vol, 1, 0.8, 1.6, dur);
    f.connect(g);
    g.connect(this.musicBus);
    g.connect(this.reverbSend);
    for (const n of notes) {
      this._osc('sawtooth', NOTE(n), t, t + dur + 2, f, -8);
      this._osc('sawtooth', NOTE(n), t, t + dur + 2, f, 9);
      this._osc('sine', NOTE(n - 12), t, t + dur + 2, f);
    }
  }
  pluck(n, t, vol = 0.06, dec = 0.6, type = 'triangle', send = true) {
    const ctx = this.ctx;
    const g = ctx.createGain();
    this._env(g, t, 0.005, vol, dec);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(4000, t);
    f.frequency.exponentialRampToValueAtTime(600, t + dec);
    f.connect(g);
    g.connect(this.musicBus);
    if (send) {
      g.connect(this.delaySend);
      g.connect(this.reverbSend);
    }
    this._osc(type, NOTE(n), t, t + dec + 0.1, f);
    this._osc('sine', NOTE(n + 12), t, t + dec + 0.1, f);
  }
  bass(n, t, dur, vol = 0.12) {
    const ctx = this.ctx;
    const g = ctx.createGain();
    this._env(g, t, 0.01, vol, 0.3, 0.6, 0.08, dur);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 420;
    f.connect(g).connect(this.musicBus);
    this._osc('sine', NOTE(n), t, t + dur + 0.3, f);
    this._osc('square', NOTE(n), t, t + dur + 0.3, f).detune.value = 3;
  }

  // ------------------------------------------------------------ songs
  playSong(name) {
    if (!this.ctx) return;
    if (name === 'menu') return this._playMenu();
    this.song = null;
    const table = globalThis.SAUCER_SONGS || {};
    if (!table[name] && !this.songBuffers[name]) {
      if (name !== 'game' && (table.game || this.songBuffers.game)) return this.playSong('game');
      return;
    }
    if (this.songName === name && this.musicSource) return;
    this.songName = name;
    this.intensity = 0;
    this.tempoMul = 1;
    this._stopMusicSource();
    const begin = () => {
      if (this.songName !== name || !this.songBuffers[name]) return;
      const src = this.ctx.createBufferSource();
      src.buffer = this.songBuffers[name];
      src.loop = true;
      src.connect(this.musicBus);
      const send = this.ctx.createGain();
      send.gain.value = 0.18;
      src.connect(send).connect(this.reverbSend);
      src.start();
      this.musicSource = src;
    };
    if (this.songBuffers[name]) begin();
    else this._decodeSong(name).then(begin);
  }
  _playMenu() {
    if (this.song && this.song.name === 'menu') return;
    this._stopMusicSource();
    this.songName = 'menu';
    this.song = { name: 'menu', ...MENU_SONG };
    this.step = 0;
    this.nextTime = this.ctx.currentTime + 0.1;
    this.intensity = 0;
    this.tempoMul = 1;
  }
  stopSong() {
    this.song = null;
    this.songName = null;
    this._stopMusicSource();
  }
  _schedule() {
    if (!this.ctx || !this.song || this.ctx.state !== 'running') return;
    const song = this.song;
    const spb = 60 / (song.bpm * (this.tempoMul || 1)) / 4;
    if (this.nextTime < this.ctx.currentTime - 0.5) this.nextTime = this.ctx.currentTime + 0.05;
    while (this.nextTime < this.ctx.currentTime + 0.15) {
      const swing = this.step % 2 === 1 ? spb * (song.swing || 0) : 0;
      try {
        song.play(this, this.step, this.nextTime + swing, spb);
      } catch (e) {
        console.warn(e);
      }
      this.nextTime += spb;
      this.step++;
    }
  }
  _stopMusicSource() {
    if (!this.musicSource) return;
    try { this.musicSource.stop(); } catch (e) { /* already stopped */ }
    this.musicSource.disconnect();
    this.musicSource = null;
  }
  _decodeSong(name) {
    const encoded = (globalThis.SAUCER_SONGS || {})[name];
    if (!encoded) return Promise.resolve();
    if (!this.songDecodes[name]) {
      const binary = atob(encoded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      this.songDecodes[name] = this.ctx.decodeAudioData(bytes.buffer).then((buffer) => {
        this.songBuffers[name] = this._closeLoop(buffer);
      }).catch(() => {});
    }
    return this.songDecodes[name];
  }
  // A short blend at the join hides the gap an mp3 decoder leaves when a buffer loops.
  _closeLoop(buffer) {
    const fade = Math.floor(buffer.sampleRate * 0.04);
    const length = buffer.length - fade;
    if (length < fade * 2) return buffer;
    const closed = this.ctx.createBuffer(buffer.numberOfChannels, length, buffer.sampleRate);
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const src = buffer.getChannelData(channel);
      const dst = closed.getChannelData(channel);
      dst.set(src.subarray(0, length));
      for (let i = 0; i < fade; i++) {
        const along = i / (fade - 1);
        const tailGain = Math.cos(along * Math.PI / 2);
        const headGain = Math.sin(along * Math.PI / 2);
        dst[length - fade + i] = src[length - fade + i] * tailGain + src[i] * headGain;
      }
    }
    return closed;
  }

  // ------------------------------------------------------------ SFX
  sfx(name, opts = {}) {
    if (!this.ctx || this.ctx.state !== 'running') return;
    const fn = SFX[name];
    if (fn) fn(this, this.ctx.currentTime + 0.005, opts);
  }
  tone(f, t, dur, type = 'sine', vol = 0.1, slideTo = 0, dest) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(dest || this.sfxBus);
    o.start(t);
    o.stop(t + dur + 0.05);
    return g;
  }
  noise(t, dur, type, freq, vol, freqTo) {
    const ctx = this.ctx;
    const s = ctx.createBufferSource();
    s.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(freq, t);
    if (freqTo) f.frequency.exponentialRampToValueAtTime(freqTo, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(f).connect(g).connect(this.sfxBus);
    s.start(t, Math.random());
    s.stop(t + dur + 0.05);
    return g;
  }
  /** continuous ambience loops (blender whirr, boiling, fire) */
  setLoop(name, level) {
    if (!this.ctx) return;
    let L = this.loops[name];
    if (!L) {
      if (level <= 0) return;
      const ctx = this.ctx;
      const g = ctx.createGain();
      g.gain.value = 0;
      g.connect(this.sfxBus);
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      src.loop = true;
      const f = ctx.createBiquadFilter();
      const def = LOOPS[name];
      f.type = def.type;
      f.frequency.value = def.freq;
      f.Q.value = def.q || 1;
      src.connect(f).connect(g);
      src.start();
      let osc = null;
      if (def.osc) {
        osc = ctx.createOscillator();
        osc.type = def.osc;
        osc.frequency.value = def.oscFreq;
        const og = ctx.createGain();
        og.gain.value = def.oscVol;
        osc.connect(og).connect(g);
        osc.start();
        if (def.lfo) {
          const lfo = ctx.createOscillator();
          lfo.frequency.value = def.lfo;
          const lg = ctx.createGain();
          lg.gain.value = def.lfoDepth;
          lfo.connect(lg).connect(osc.frequency);
          lfo.start();
        }
      }
      L = this.loops[name] = { g, src, osc, f, def };
    }
    L.g.gain.setTargetAtTime(Math.max(0, level) * L.def.vol, this.ctx.currentTime, 0.08);
  }
  stopLoops() {
    for (const k in this.loops) this.loops[k].g.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
  }
}

const LOOPS = {
  blender: { type: 'bandpass', freq: 900, q: 3, vol: 0.12, osc: 'sawtooth', oscFreq: 110, oscVol: 0.25, lfo: 9, lfoDepth: 8 },
  boil: { type: 'lowpass', freq: 500, q: 1, vol: 0.12 },
  fire: { type: 'lowpass', freq: 900, q: 0.5, vol: 0.3 },
  whistle: { type: 'bandpass', freq: 2400, q: 12, vol: 0.2, osc: 'sine', oscFreq: 1850, oscVol: 0.06, lfo: 7, lfoDepth: 40 },
  hum: { type: 'lowpass', freq: 160, q: 1, vol: 0.12, osc: 'sine', oscFreq: 55, oscVol: 0.4 },
};

const SFX = {
  pickup: (a, t) => { a.tone(520, t, 0.08, 'triangle', 0.12, 780); },
  drop: (a, t) => { a.tone(300, t, 0.1, 'triangle', 0.12, 180); a.noise(t, 0.05, 'lowpass', 900, 0.08); },
  place: (a, t) => { a.tone(240, t, 0.08, 'sine', 0.18, 140); a.noise(t, 0.04, 'bandpass', 1500, 0.06); },
  chop: (a, t) => { a.noise(t, 0.06, 'bandpass', 2600 + Math.random() * 800, 0.22); a.tone(180, t, 0.05, 'square', 0.04, 90); },
  grind: (a, t) => { a.noise(t, 0.12, 'bandpass', 700 + Math.random() * 300, 0.2, 1200); },
  prepDone: (a, t) => { a.tone(880, t, 0.1, 'sine', 0.1); a.tone(1320, t + 0.07, 0.15, 'sine', 0.1); },
  addIng: (a, t) => { a.noise(t, 0.15, 'lowpass', 1200, 0.15, 300); a.tone(400, t, 0.1, 'sine', 0.08, 250); },
  brewDone: (a, t) => { [0, 4, 7, 12].forEach((n, i) => a.tone(NOTE(79 + n), t + i * 0.06, 0.4, 'sine', 0.08)); },
  pour: (a, t) => { a.noise(t, 0.45, 'bandpass', 600, 0.25, 1600); },
  // cup sealer: a heavy press thunk, then the hiss of hot film
  seal: (a, t) => { a.tone(120, t, 0.12, 'square', 0.1, 70); a.noise(t, 0.08, 'lowpass', 800, 0.25); a.noise(t + 0.06, 0.4, 'highpass', 3500, 0.1, 7000); a.tone(NOTE(84), t + 0.3, 0.15, 'sine', 0.06); },
  // straw punching through the seal film
  straw: (a, t) => { a.noise(t, 0.04, 'bandpass', 2200, 0.3); a.tone(900, t, 0.09, 'triangle', 0.1, 300); a.tone(NOTE(88), t + 0.07, 0.12, 'sine', 0.06); },
  fizz: (a, t) => { a.noise(t, 0.8, 'highpass', 5000, 0.12, 9000); for (let i = 0; i < 6; i++) a.tone(1800 + Math.random() * 1600, t + i * 0.07, 0.04, 'sine', 0.04); },
  serve: (a, t) => { [0, 4, 7, 11, 14].forEach((n, i) => a.tone(NOTE(76 + n), t + i * 0.05, 0.5, 'triangle', 0.09)); },
  coin: (a, t) => { a.tone(NOTE(88), t, 0.08, 'square', 0.05); a.tone(NOTE(95), t + 0.07, 0.3, 'square', 0.05); },
  zap: (a, t) => {
    // electric crack + buzzing sizzle
    a.noise(t, 0.05, 'highpass', 2500, 0.45, 9000);
    for (let i = 0; i < 7; i++) a.tone(90 + Math.random() * 60, t + i * 0.025, 0.035, 'sawtooth', 0.12, 40);
    a.noise(t + 0.03, 0.35, 'bandpass', 3200, 0.2, 700);
    a.tone(1400, t, 0.25, 'square', 0.03, 180);
  },
  arcTick: (a, t) => { a.noise(t, 0.03, 'highpass', 5000, 0.04, 9000); },
  wrong: (a, t) => { a.tone(220, t, 0.18, 'sawtooth', 0.07, 160); a.tone(207, t + 0.12, 0.25, 'sawtooth', 0.07, 140); },
  angry: (a, t) => { a.tone(180, t, 0.35, 'sawtooth', 0.09, 90); a.noise(t, 0.3, 'lowpass', 600, 0.1); },
  guestArrive: (a, t) => { a.tone(NOTE(84), t, 0.5, 'sine', 0.06, NOTE(96)); a.noise(t, 0.6, 'highpass', 3000, 0.05, 8000); },
  teleport: (a, t) => { a.tone(300, t, 0.25, 'sawtooth', 0.05, 2400); a.tone(2400, t + 0.1, 0.2, 'sine', 0.05, 600); },
  dash: (a, t) => { a.noise(t, 0.2, 'bandpass', 900, 0.14, 3000); },
  throw: (a, t) => { a.noise(t, 0.18, 'bandpass', 1400, 0.1, 500); },
  ignite: (a, t) => { a.noise(t, 0.6, 'lowpass', 300, 0.35, 2400); a.tone(90, t, 0.5, 'sawtooth', 0.08, 50); },
  extinguish: (a, t) => { a.noise(t, 0.25, 'highpass', 2500, 0.15, 5000); },
  wash: (a, t) => { a.noise(t, 0.15, 'bandpass', 1200 + Math.random() * 500, 0.12); },
  clean: (a, t) => { a.tone(NOTE(91), t, 0.12, 'sine', 0.08); a.tone(NOTE(98), t + 0.06, 0.2, 'sine', 0.06); },
  burnt: (a, t) => { a.tone(160, t, 0.6, 'sawtooth', 0.1, 60); a.noise(t, 0.5, 'lowpass', 700, 0.2); },
  perk: (a, t) => { [0, 7, 12, 16, 19, 24].forEach((n, i) => a.tone(NOTE(72 + n), t + i * 0.045, 0.6, 'sine', 0.07)); },
  swap: (a, t) => { a.tone(600, t, 0.12, 'sine', 0.08, 900); a.tone(900, t + 0.08, 0.12, 'sine', 0.06, 1200); },
  wheelOpen: (a, t) => { a.tone(NOTE(79), t, 0.1, 'sine', 0.06, NOTE(86)); a.noise(t, 0.12, 'highpass', 4000, 0.04, 8000); },
  wheelTick: (a, t) => { a.tone(NOTE(93), t, 0.04, 'triangle', 0.045); },
  delegateOn: (a, t) => { [0, 4, 7].forEach((n, i) => a.tone(NOTE(79 + n), t + i * 0.05, 0.25, 'triangle', 0.07)); },
  delegateOff: (a, t) => { [7, 0].forEach((n, i) => a.tone(NOTE(76 + n), t + i * 0.06, 0.2, 'sine', 0.06)); },
  uiHover: (a, t) => { a.tone(NOTE(96), t, 0.05, 'sine', 0.025); },
  uiClick: (a, t) => { a.tone(NOTE(84), t, 0.07, 'triangle', 0.08); a.tone(NOTE(91), t + 0.04, 0.12, 'sine', 0.06); },
  uiBack: (a, t) => { a.tone(NOTE(79), t, 0.1, 'triangle', 0.07, NOTE(72)); },
  countdown: (a, t, o) => { a.tone(o.go ? NOTE(84) : NOTE(72), t, o.go ? 0.6 : 0.2, 'square', 0.06); },
  timeWarn: (a, t) => { a.tone(NOTE(81), t, 0.12, 'square', 0.05); },
  star: (a, t, o) => { const b = 76 + (o.i || 0) * 4; [0, 7, 12].forEach((n, i) => a.tone(NOTE(b + n), t + i * 0.05, 0.7, 'triangle', 0.08)); a.noise(t, 0.5, 'highpass', 6000, 0.06); },
  fanfare: (a, t) => { [[72, 76, 79], [74, 77, 81], [76, 79, 84]].forEach((ch, i) => ch.forEach((n) => a.tone(NOTE(n), t + i * 0.18, 0.8, 'triangle', 0.06))); },
  fail: (a, t) => { [67, 63, 60].forEach((n, i) => a.tone(NOTE(n), t + i * 0.2, 0.5, 'triangle', 0.07)); },
  warning: (a, t) => { for (let i = 0; i < 3; i++) a.tone(NOTE(88), t + i * 0.18, 0.1, 'square', 0.05); },
  bridge: (a, t) => { a.tone(80, t, 0.8, 'sawtooth', 0.08, 50); a.noise(t, 0.8, 'lowpass', 400, 0.12); },
  blackout: (a, t) => { a.tone(220, t, 1.0, 'sawtooth', 0.07, 40); },
  powerOn: (a, t) => { a.tone(60, t, 0.8, 'sawtooth', 0.06, 240); },
};

const MENU_CHORDS = {
  Fmaj9: [53, 57, 60, 64, 67],
  Em7: [52, 55, 59, 62],
  Dm9: [50, 53, 57, 60, 64],
  Cmaj7: [48, 52, 55, 59],
};

const MENU_SONG = {
  bpm: 68,
  swing: 0,
  play(a, s, t, spb) {
    const bar = Math.floor(s / 16) % 4;
    const prog = [MENU_CHORDS.Fmaj9, MENU_CHORDS.Em7, MENU_CHORDS.Dm9, MENU_CHORDS.Cmaj7];
    const ch = prog[bar];
    if (s % 16 === 0) {
      a.pad(ch, t, spb * 16, 0.028, 1100);
      a.bass(ch[0] - 12, t, spb * 14, 0.07);
    }
    const arp = [0, 2, 1, 3, 2, 4, 1, 3];
    if (s % 2 === 0) {
      const n = ch[arp[(s / 2) % 8] % ch.length] + 24;
      if (Math.random() < 0.8) a.pluck(n, t, 0.022, 0.9, 'sine');
    }
    if (s % 32 === 30 && Math.random() < 0.7) a.pluck(ch[ch.length - 1] + 36, t, 0.014, 2, 'sine');
  },
};

export const audio = new AudioEngine();
